import api from "@/api/axios";
import { eraseCookie, getCookie, setCookie } from "@/utils/cookie";
import type {
  AppleTokenLoginPayload,
  CustomerAuthResponse,
  CustomerLoginPayload,
  CustomerRegisterPayload,
  ForgotPasswordPayload,
  ForgotPasswordResponse,
  ResetPasswordPayload,
  ResetPasswordResponse,
  VerifyEmailPayload,
  VerifyEmailResponse,
  VerifyOtpResponse,
  VerifyResetOtpPayload,
} from "@/types/customer-auth.types";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "/api";

// ============================================
// Customer Auth Service
// ============================================

export const customerAuthService = {
  /**
   * Register new customer account
   * Account will be in Pending state until email is verified
   */
  register: async (payload: CustomerRegisterPayload): Promise<void> => {
    await api.post("/auth-customer/register", payload);
  },

  /**
   * Verify email with OTP code
   * Returns user data after successful verification
   */
  verifyEmail: async (
    payload: VerifyEmailPayload,
  ): Promise<VerifyEmailResponse> => {
    const response = await api.post<VerifyEmailResponse>(
      "/auth-customer/verify-email",
      payload,
    );
    return response.data;
  },

  /**
   * Login with email and password
   * Returns user data and tokens
   */
  login: async (
    payload: CustomerLoginPayload,
  ): Promise<CustomerAuthResponse> => {
    const response = await api.post<CustomerAuthResponse>(
      "/auth-customer/login",
      payload,
    );
    return response.data;
  },

  /**
   * Request password reset OTP
   * Sends OTP to user's email
   */
  forgotPassword: async (
    payload: ForgotPasswordPayload,
  ): Promise<ForgotPasswordResponse> => {
    const response = await api.post<ForgotPasswordResponse>(
      "/auth-customer/otp/forgot-password",
      payload,
    );
    return response.data;
  },

  /**
   * Verify reset password OTP
   * Returns whether OTP is valid
   */
  verifyResetOtp: async (
    payload: VerifyResetOtpPayload,
  ): Promise<VerifyOtpResponse> => {
    const response = await api.post<VerifyOtpResponse>(
      "/auth-customer/otp/verify-reset",
      payload,
    );
    return response.data;
  },

  /**
   * Reset password with OTP
   */
  resetPassword: async (
    payload: ResetPasswordPayload,
  ): Promise<ResetPasswordResponse> => {
    const response = await api.post<ResetPasswordResponse>(
      "/auth-customer/otp/reset-password",
      payload,
    );
    return response.data;
  },

  /**
   * Login with Apple ID token (client-side Sign in with Apple)
   */
  appleTokenLogin: async (
    payload: AppleTokenLoginPayload,
  ): Promise<CustomerAuthResponse> => {
    const response = await api.post<CustomerAuthResponse>(
      "/auth-customer/apple/token",
      payload,
    );
    return response.data;
  },

  /**
   * Resend verification email
   * Re-registers the user to trigger new OTP email
   */
  resendVerificationEmail: async (
    email: string,
    password: string,
    fullName: string,
  ): Promise<void> => {
    await api.post("/auth-customer/register", {
      email,
      password,
      full_name: fullName,
    });
  },
};

// ============================================
// Social Login URLs
// ============================================

export const socialLoginUrls = {
  google: `${API_BASE_URL}/auth-customer/google`,
  facebook: `${API_BASE_URL}/auth-customer/facebook`,
  apple: `${API_BASE_URL}/auth-customer/apple`,
};

// ============================================
// Token Management (for customer auth)
// ============================================

const TOKEN_KEY = "customer_access_token";
const REFRESH_TOKEN_KEY = "customer_refresh_token";
const USER_KEY = "customer_user";
const TYPE_LOGIN_KEY = "type_login";

// 30 days expiration for persistent sessions across iOS standalone and Safari
const COOKIE_MAX_AGE_SECONDS = 30 * 24 * 60 * 60;

export const customerTokenService = {
  getType(): string | null {
    let type: string | null = null;
    try {
      type = localStorage.getItem(TYPE_LOGIN_KEY);
    } catch {
      // ignore storage access errors
    }

    if (!type) {
      type = getCookie(TYPE_LOGIN_KEY);
      if (type) {
        try {
          localStorage.setItem(TYPE_LOGIN_KEY, type);
        } catch {}
      }
    }

    return type;
  },

  setType(type: "store" | "customer"): void {
    try {
      localStorage.setItem(TYPE_LOGIN_KEY, type);
    } catch {}
    setCookie(TYPE_LOGIN_KEY, type, COOKIE_MAX_AGE_SECONDS);
  },

  getAccessToken: (): string | null => {
    let token: string | null = null;
    try {
      token = localStorage.getItem(TOKEN_KEY);
    } catch {}

    if (!token) {
      token = getCookie(TOKEN_KEY);
      if (token) {
        try {
          localStorage.setItem(TOKEN_KEY, token);
        } catch {}
      }
    }

    return token;
  },

  setAccessToken: (token: string): void => {
    try {
      localStorage.setItem(TOKEN_KEY, token);
    } catch {}
    setCookie(TOKEN_KEY, token, COOKIE_MAX_AGE_SECONDS);
  },

  getRefreshToken: (): string | null => {
    let refreshToken: string | null = null;
    try {
      refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    } catch {}

    if (!refreshToken) {
      refreshToken = getCookie(REFRESH_TOKEN_KEY);
      if (refreshToken) {
        try {
          localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
        } catch {}
      }
    }

    return refreshToken;
  },

  setRefreshToken: (token: string): void => {
    try {
      localStorage.setItem(REFRESH_TOKEN_KEY, token);
    } catch {}
    setCookie(REFRESH_TOKEN_KEY, token, COOKIE_MAX_AGE_SECONDS);
  },

  getUser: () => {
    let userStr: string | null = null;
    try {
      userStr = localStorage.getItem(USER_KEY);
    } catch {}

    if (!userStr) {
      userStr = getCookie(USER_KEY);
      if (userStr) {
        try {
          localStorage.setItem(USER_KEY, userStr);
        } catch {}
      }
    }

    if (!userStr) return null;
    try {
      return JSON.parse(userStr);
    } catch {
      return null;
    }
  },

  setUser: (user: unknown): void => {
    if (!user) return;
    const userStr = JSON.stringify(user);
    try {
      localStorage.setItem(USER_KEY, userStr);
    } catch {}
    setCookie(USER_KEY, userStr, COOKIE_MAX_AGE_SECONDS);
  },

  saveAuthData: (data: CustomerAuthResponse): void => {
    customerTokenService.setAccessToken(data.access_token);
    customerTokenService.setRefreshToken(data.refresh_token);
    customerTokenService.setUser(data.user);
    customerTokenService.setType("customer");
  },

  clearAuth: (): void => {
    try {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      localStorage.removeItem(TYPE_LOGIN_KEY);
    } catch {}

    eraseCookie(TOKEN_KEY);
    eraseCookie(REFRESH_TOKEN_KEY);
    eraseCookie(USER_KEY);
    eraseCookie(TYPE_LOGIN_KEY);
    eraseCookie("accessToken");
    eraseCookie("refreshToken");
  },

  isAuthenticated: (): boolean => {
    return !!customerTokenService.getAccessToken();
  },
};

export default customerAuthService;
